import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Sparkles, BookOpen, Users, ArrowRight } from 'lucide-react';

const About: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4A5A5]/10 to-[#9CAF88]/10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <span className="font-sans text-sm text-[#D4A5A5] font-medium tracking-wider uppercase mb-4 block">Our Story</span>
              <h1 className="font-serif text-5xl sm:text-6xl font-semibold text-[#3D3D3D] leading-tight mb-6">
                Creating Space for
                <br />
                <span className="text-[#D4A5A5]">God to meet us creatively</span>
              </h1>
              <p className="font-sans text-lg text-[#3D3D3D]/70 leading-relaxed mb-8">
                <b>If we are created in the image of our creator, weren't we also created to create?</b>
                <br />
                What better way to meet with God, than by imitating Him! NOT JuNK Journaling was born out of a desire to gather ladies to hear God’s word through meditation, crafts and encouragement! 
              </p>
              <p className="font-sans text-[#3D3D3D]/70 leading-relaxed">
                What started as a personal creative practice, blossomed into a community of women who also want to create space for God to move through them, intertwining the word of God and art. 
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
              <div className="relative">
                <img
                  src={`${process.env.PUBLIC_URL}/img/journaling-bg.png`}
                  alt="Bible journaling"
                  className="rounded-3xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#D4A5A5]/20 rounded-full blur-2xl" />
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#9CAF88]/20 rounded-full blur-2xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-semibold text-[#3D3D3D] mb-4">What We Believe</h2>
            <p className="font-sans text-[#3D3D3D]/70 max-w-2xl mx-auto">
              Encouragement is Important. Our heart is to give women hope that they can live in a deeper walk with God through meditation in the Word and by recognizing He has given us all unique creative gifts.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: BookOpen,
                title: 'Scripture First',
                description:
                  "Every design we create is rooted in God's Word. We believe the Bible is living and active, and our resources are meant to help you engage with it more deeply.",
                color: 'rose',
              },
              {
                icon: Heart,
                title: 'Creativity as Worship',
                description:
                  "We believe creativity is a gift from God. When we create, we reflect our Creator. There's no right or wrong way to journal—just your unique expression.",
                color: 'green',
              },
              {
                icon: Users,
                title: 'Community Matters',
                description:
                  "We're not meant to walk this journey alone. Our community of journalers encourages, inspires, and prays for one another.",
                color: 'amber',
              },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-center p-8">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6
                    ${value.color === 'rose' ? 'bg-rose-300/20' : ''}
                    ${value.color === 'green' ? 'bg-green-300/20' : ''}
                    ${value.color === 'amber' ? 'bg-amber-300/20' : ''}
                  `}>
                    <value.icon className={`w-8 h-8
                      ${value.color === 'rose' ? 'text-rose-400' : ''}
                      ${value.color === 'green' ? 'text-green-500' : ''}
                      ${value.color === 'amber' ? 'text-amber-500' : ''}
                    `} />
                  </div>
                  <h3 className="font-serif text-2xl font-semibold text-[#3D3D3D] mb-4">{value.title}</h3>
                  <p className="font-sans text-[#3D3D3D]/70 leading-relaxed">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-[#3D3D3D] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4A5A5]/10 rounded-full blur-3xl" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <Sparkles className="w-12 h-12 text-[#C9A962] mx-auto mb-6" />
            <h2 className="font-serif text-4xl sm:text-5xl font-semibold mb-6">Our Mission</h2>
            <p className="font-sans text-xl text-white/80 leading-relaxed mb-8">
              "To create beautiful, thoughtful resources that inspire women to spend
              time in God's Word and express their faith through creativity."
            </p>
            <p className="font-sans text-white/60">
              Every page we design, every workshop we host, and every resource we create
              is made with prayer and intention, hoping it will be a blessing in your
              spiritual journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      {/* <section className="py-24 bg-[#FDF8F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-serif text-4xl font-semibold text-[#3D3D3D] mb-6">Ready to Start Your Journey?</h2>
            <p className="font-sans text-lg text-[#3D3D3D]/70 mb-8 max-w-2xl mx-auto">
              Whether you're brand new to Bible journaling or looking to deepen your practice,
              we're here to help you every step of the way.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/products">
                <button className="bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white px-6 py-3 rounded-full font-sans text-sm leading-none group inline-flex items-center justify-center whitespace-nowrap">
                  <span className="inline-block">Browse the Shop</span>
                  <ArrowRight className="ml-1.5 w-4 h-4 inline-block group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <Link to="/subscriptions">
                <button className="border border-[#D4A5A5] text-[#D4A5A5] hover:bg-[#D4A5A5]/10 px-6 py-3 rounded-full font-sans text-sm leading-none">
                  Join the Membership
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section> */}
    </div>
  );
};

export default About;